import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetOverviewErrors } from '@casops/hooks/useAssetOverviewErrors';
import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';
import { NavTabModel } from '@cas/components/tabs/nav-tab/NavTab';
import { Permission, useIsUserWithPermission } from '@casops/permissions';

export interface useAssetPageTabsProps {
  assetId: string | undefined;
  assetOverviewErrors: AssetOverviewErrors;
  assetDeviceType: keyof typeof AssetDeviceType | undefined;
  setTabs: (tabs: NavTabModel[]) => void;
}

export const useAssetPageTabs = ({
  assetId,
  assetOverviewErrors,
  assetDeviceType,
  setTabs,
}: useAssetPageTabsProps) => {
  const { t } = useTranslation();
  const assetPath = `/assets/asset/${assetId}`;

  const userIsAdmin = useIsUserWithPermission(Permission.Admin);

  const tabs = useMemo(() => {
    const tabs = [
      {
        title: t('Navigation.AssetDetails'),
        to: `${assetPath}/details`,
        isDotted: assetOverviewErrors.reporting,
      },
    ];

    const conditionalTabs = [
      {
        title: t('Navigation.AssetConfiguration'),
        to: `${assetPath}/configuration`,
        isDotted: assetOverviewErrors.config,
      },
      {
        title: t('Navigation.AssetSelfTest'),
        to: `${assetPath}/self-test`,
        isDotted: assetOverviewErrors.activeFaults,
      },
    ];

    if (userIsAdmin) {
      conditionalTabs.push({
        title: t('Navigation.Diagnostics'),
        to: `${assetPath}/diagnostics`,
        isDotted: false,
      });
    }

    return assetDeviceType !== 'PPU' ? [...tabs, ...conditionalTabs] : tabs;
  }, [
    t,
    assetPath,
    assetOverviewErrors.reporting,
    assetOverviewErrors.config,
    assetOverviewErrors.activeFaults,
    userIsAdmin,
    assetDeviceType,
  ]);

  useEffect(() => {
    setTabs(tabs);
  }, [setTabs, tabs]);
};

export default useAssetPageTabs;
